import { Directive, ElementRef, inject, OnDestroy, PLATFORM_ID, Renderer2 } from '@angular/core';
import { Observable, of, Subject, filter, switchMap, take, tap, Subscription } from 'rxjs';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

import { CmsComponentData } from '@spartacus/storefront';
import { CmsComponent } from '@spartacus/core';

import { BrandsEnum, IMedia } from '@seb/web-design-system/core';

import { LazyLoadingService } from '../../services/lazy-loading.service';
import { BaseStoreService } from '../../services/base-store.service';
import { Wrapper } from '../../models/wrapper.model';

@Directive()
export abstract class AbstractComponent<T extends CmsComponent> implements OnDestroy {
  protected lazyLoadingService = inject(LazyLoadingService);
  protected baseStoreService = inject(BaseStoreService);
  protected component = inject(CmsComponentData<T>);
  protected platformId = inject(PLATFORM_ID);
  protected renderer = inject(Renderer2);
  protected elRef = inject(ElementRef);

  protected component$: Observable<T>;
  protected componentWithoutLazyLoad$: Observable<T>;

  protected readonly destroy$: Subject<void> = new Subject();
  protected subscriptions = new Subscription();

  componentUid: string;
  isFirst = false;
  isLoaded = false;
  isIncomplete = false;
  wrapper: Wrapper;
  brandCorner: BrandsEnum;
  brandLogo: IMedia;
  useLazyLoad = false;

  constructor() {
    this.useLazyLoad = this.lazyLoadingService.isEnabled;
    this.componentUid = this.component.uid;

    this.componentWithoutLazyLoad$ = this.getComponentWithoutLazyLoad();

    this.component$ = (this.useLazyLoad ? this.getComponentWithLazyLoad() : this.componentWithoutLazyLoad$).pipe(
      tap((cmp) => {
        this.brandCorner = this.getBrandCorner(cmp);
        this.wrapper = { ...cmp };
      })
    );

    if (isPlatformServer(this.platformId)) {
      this.subscriptions.add(
        this.lazyLoadingService.firstUids.subscribe((firstUids) => {
          this.isFirst = firstUids.includes(this.component.uid) || firstUids.length === 0;
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  mandatoryFunction = (_module: CmsComponent): boolean => {
    return true;
  };

  setLoaded(): void {
    this.isLoaded = true;
    if (this.isFirst) {
      this.lazyLoadingService.setUidLoaded(this.component.uid);
    }
  }

  private getBrandCorner(cmp: CmsComponent): BrandsEnum {
    const brand = this.baseStoreService.getBrand(cmp['brandCorner']);
    return (brand?.id as BrandsEnum) || BrandsEnum.default;
  }

  private getComponentWithLazyLoad(): Observable<T> {
    return this.lazyLoadingService.firstUids.pipe(
      switchMap((firstUids) => {
        this.isFirst = firstUids.includes(this.component.uid) || firstUids.length === 0;
        return (this.lazyLoadingService.isEnabled || this.lazyLoadingService.isForced) && !this.isFirst
          ? this.lazyLoadingService.canLazyLoad.pipe(
            filter((canLazyLoad) => canLazyLoad),
            take(1)
          )
          : of(true);
      }),
      switchMap(() => this.component.data$),
      filter((cmp) => !!cmp),
      filter((cmp) => {
        this.setComponentId(cmp);
        this.isIncomplete = this.mandatoryFunction(cmp) === false;
        if (this.isIncomplete) {
          this.setLoaded();
        }
        return !this.isIncomplete;
      })
    );
  }

  private getComponentWithoutLazyLoad(): Observable<T> {
    return this.component.data$.pipe(
      filter((cmp) => !!cmp),
      filter((cmp) => {
        this.setComponentId(cmp);
        return this.mandatoryFunction(cmp);
      })
    );
  }

  private setComponentId(cmp: T): void {
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.setProperty(this.elRef.nativeElement, 'id', cmp.typeCode + '_' + cmp.uid);
    }
  }
}
